import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Box, Button, styled, Typography } from "@mui/material";
import {
  useCriticalActivitiesMutation,
  useFetchCoreActivityQuery,
  useFetchCoreFocusAreasQuery,
} from "../../../services/roleReviewService";
import useAuth from "../../../hooks/useAuth";
import { Link, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import CoreFocusAreaActivitiesCard from "./CoreFocusAreaActivitiesCard";
const sortCoreFocusAreasByPosition = (areas) => {
  const list = Array.isArray(areas) ? [...areas] : [];
  return list.sort((a, b) => {
    const aPos = Number(a?.position);
    const bPos = Number(b?.position);
    const aHas = Number.isFinite(aPos);
    const bHas = Number.isFinite(bPos);

    if (aHas && bHas) return aPos - bPos;
    if (aHas) return -1;
    if (bHas) return 1;
    return 0;
  });
};
const validationSchema = yup.object().shape({
  coreFocusAreas: yup
    .array()
    .of(
      yup.object().shape({
        subData: yup
          .array()
          .of(
            yup.object().shape({
              subTask: yup.string().required("Subtask is required"),
              timeSpent: yup
                .number()
                .typeError("Time Spent must be a valid number")
                .min(1, "Must be at least 1")
                .max(100, "Cannot exceed 100")
                .required("Time Spent is required"),
            })
          )
          .required("At least one Subtask is required"),
      })
    )
    .required("Core Focus Areas are required"),
});

const Step3 = ({ onPrev, formData, onNext }) => {
  const { users } = useAuth();
  // const userId = useParams();
  // const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  // const [isJwtUser, setIsJwtUser] = useState(false);
  
  const { id: param } = useParams();
  
  const [employeeId, setEmployeeId] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [autoSaveError, setAutoSaveError] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const skipNextAutoSaveRef = useRef(true);
  const draftSaveTimerRef = useRef(null);
  const skipNextDraftSaveRef = useRef(true);
  const draftKey = employeeId ? `roleReview:step3:${employeeId}` : null;
    useEffect(() => {
      if (users?.role === "manager") {
        if (param.includes(".")) {
          try {
            const decoded = jwtDecode(param); // Decode JWT token
            setEmployeeId(decoded?.user_id);
            // setIsJwtUser(true);  // Set employeeId in state
          } catch (error) {
            console.error("Invalid token:", error);
            setEmployeeId(null);
          }
        } else {
          setEmployeeId(param);
          // setIsJwtUser(false);  // If not a token, use the param as employee ID
        }
      } else {
        setEmployeeId(users?.user_id); // Use logged-in user ID for non-managers
      }
    }, [users, param]);
 
  // const navigate = useNavigate();
  const [createCriticalActivity] = useCriticalActivitiesMutation();
  const { data: fetchedActivities, refetch: fetchactivitiesdata } = useFetchCoreActivityQuery(employeeId, {
    skip: !employeeId,
  });
  const { data: fetchedCoreFocusAreas, refetch: refetchCoreFocusAreas } =
    useFetchCoreFocusAreasQuery(employeeId, {
      skip: !employeeId,
    });
  
  useEffect(() => {
    if (!employeeId) return;
    fetchactivitiesdata();
    refetchCoreFocusAreas();
    skipNextAutoSaveRef.current = true;
    skipNextDraftSaveRef.current = true;
  }, [employeeId, fetchactivitiesdata, refetchCoreFocusAreas]);
  // const processedFormData = {
  //   ...formData,
  //   coreFocusAreas: formData.coreFocusAreas.map((focusArea) => ({
  //     ...focusArea,
  //     subData: focusArea.subData?.length > 0 ? focusArea.subData : [{ subTask: "", timeSpent: "" }],
  //   })),
  // };
  const processedFormData = useMemo(() => {
    const apiAreas = Array.isArray(fetchedCoreFocusAreas?.data) ? fetchedCoreFocusAreas.data : [];
    const parentAreas = Array.isArray(formData?.coreFocusAreas) ? formData.coreFocusAreas : [];
    // After Step 2 "Next", parent state holds the mutation response and is authoritative.
    // RTK Query cache can still be stale until refetch completes, so prefer parent when present.
    const sourceCoreFocusAreas = parentAreas.length > 0 ? parentAreas : apiAreas;
    const orderedCoreFocusAreas = sortCoreFocusAreasByPosition(sourceCoreFocusAreas);

    const coreFocusAreas = orderedCoreFocusAreas.map((focusArea) => {
      const relatedActivities = fetchedActivities?.data?.filter(
        (activity) => activity.core_focus_area_id === focusArea.id
      );

      return {
        ...focusArea,
        subData:
          relatedActivities?.length > 0
            ? relatedActivities.map((activity) => ({
                subTask: activity.area || "",
                timeSpent: activity.importance || "",
              }))
            : [
                { subTask: "", timeSpent: "" },
                { subTask: "", timeSpent: "" },
                { subTask: "", timeSpent: "" },
              ],
      };
    });

    return { ...formData, coreFocusAreas };
  }, [formData, fetchedActivities?.data, fetchedCoreFocusAreas?.data]);
  

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: processedFormData,
    resolver: yupResolver(validationSchema),
  });

  // When Step 2 updates reorder/positions and user comes back to Step 3,
  // make sure we refetch + reset the Step 3 form to the latest ordering.
  useEffect(() => {
    if (!employeeId) return;
    // If parent passed new Step 2 data, refetch the persisted order from backend.
    refetchCoreFocusAreas();
    fetchactivitiesdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, formData?.coreFocusAreas]);

  // Re-sync RHF state when core focus areas ordering changes (preserve existing subData by id).
  useEffect(() => {
    const nextCoreFocusAreas = processedFormData?.coreFocusAreas || [];
    if (!Array.isArray(nextCoreFocusAreas) || nextCoreFocusAreas.length === 0) return;

    const current = getValues("coreFocusAreas") || [];
    const currentById = new Map(
      (current || [])
        .filter((a) => a && a.id != null)
        .map((a) => [a.id, a])
    );

    const merged = nextCoreFocusAreas.map((area) => {
      const existing = currentById.get(area?.id);
      if (!existing) return area;
      return {
        ...area,
        subData: Array.isArray(existing?.subData) && existing.subData.length > 0 ? existing.subData : area.subData,
      };
    });

    reset({ ...processedFormData, coreFocusAreas: merged });
    skipNextAutoSaveRef.current = true;
    skipNextDraftSaveRef.current = true;
  }, [processedFormData, getValues, reset]);

  // Restore draft when coming back to Step 3 (Previous -> Next).
  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.coreFocusAreas)) return;
      const latestAreas = sortCoreFocusAreasByPosition(processedFormData?.coreFocusAreas || []);
      const draftById = new Map(
        parsed.coreFocusAreas.filter((a) => a && a.id != null).map((a) => [a.id, a])
      );
      // Merge so newly added focus areas (from Step 2) still appear; keep saved subData by id.
      const mergedFromDraft = latestAreas.map((area) => {
        const draft = draftById.get(area?.id);
        if (!draft) return area;
        return {
          ...area,
          subData:
            Array.isArray(draft.subData) && draft.subData.length > 0 ? draft.subData : area.subData,
        };
      });
      reset({
        ...processedFormData,
        coreFocusAreas: mergedFromDraft,
      });
      skipNextAutoSaveRef.current = true;
      skipNextDraftSaveRef.current = true;
    } catch (e) {
      console.warn("Failed to restore Step 3 draft:", e);
    }
  }, [draftKey, processedFormData, reset]);

  // Persist draft locally on every change (debounced) so navigation doesn't lose edits.
  useEffect(() => {
    if (!draftKey) return;
    const subscription = watch((values) => {
      if (skipNextDraftSaveRef.current) {
        skipNextDraftSaveRef.current = false;
        return;
      }
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = setTimeout(() => {
        try {
          localStorage.setItem(
            draftKey,
            JSON.stringify({ coreFocusAreas: values?.coreFocusAreas || [] })
          );
        } catch (e) {
          // If storage is full or unavailable, fail silently.
          console.warn("Failed to save Step 3 draft:", e);
        }
      }, 250);
    });

    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
      subscription?.unsubscribe?.();
    };
  }, [draftKey, watch]);

  // Define useFieldArray for coreFocusAreas
  // IMPORTANT: avoid conflict between RHF FieldArray's internal `id` and backend `id`
  const { fields: coreFocusFields } = useFieldArray({
    control,
    name: "coreFocusAreas",
    keyName: "fieldId",
  });
  // useEffect(() => {
  //   if (fetchedActivities?.data) {
  //     console.log("Fetched Activities:", fetchedActivities.data);
  
  //     // Create a mapping to store activities grouped by core_focus_area_id
  //     const focusAreaMap = {};
  
  //     fetchedActivities.data.forEach((activity) => {
  //       // Find the corresponding core focus area index
  //       const focusAreaIndex = formData.coreFocusAreas.findIndex(
  //         (focusArea) => focusArea.id === activity.core_focus_area_id
  //       );
  
  //       if (focusAreaIndex >= 0) {
  //         // If the focus area is already mapped, push new data; otherwise, initialize array
  //         if (!focusAreaMap[focusAreaIndex]) {
  //           focusAreaMap[focusAreaIndex] = [];
  //         }
  //         focusAreaMap[focusAreaIndex].push({
  //           subTask: activity.area,
  //           timeSpent: activity.importance,
  //         });
  //       }
  //     });
  
  //     // Update the form state with all collected subData for each core focus area
  //     Object.keys(focusAreaMap).forEach((index) => {
  //       setValue(`coreFocusAreas.${index}.subData`, focusAreaMap[index]);
  //     });
  //   }
  // }, [fetchedActivities, formData.coreFocusAreas, setValue]);
  useEffect(() => {
    if (fetchedActivities?.data) {
      const focusAreaMap = {};
  
      fetchedActivities.data.forEach((activity) => {
        const focusAreaIndex = processedFormData?.coreFocusAreas?.findIndex(
          (focusArea) => focusArea.id === activity.core_focus_area_id
        );
  
        if (focusAreaIndex >= 0) {
          if (!focusAreaMap[focusAreaIndex]) {
            focusAreaMap[focusAreaIndex] = [];
          }
          focusAreaMap[focusAreaIndex].push({
            subTask: activity.area,
            timeSpent: activity.importance,
          });
        }
      });
  
      Object.keys(focusAreaMap).forEach((index) => {
        // Set only if no prefilled data exists
        if (!focusAreaMap[index] || focusAreaMap[index].length === 0) {
          focusAreaMap[index] = [
            { subTask: "", timeSpent: "" },
            { subTask: "", timeSpent: "" },
            { subTask: "", timeSpent: "" },
          ];
        }
        setValue(`coreFocusAreas.${index}.subData`, focusAreaMap[index]);
      });
  
      // Handle cases where coreFocusAreas have no activities and no prefilled data
      (processedFormData?.coreFocusAreas || []).forEach((focusArea, index) => {
        const existingSubData = watch(`coreFocusAreas.${index}.subData`);
        if (!existingSubData || existingSubData.length === 0) {
          setValue(`coreFocusAreas.${index}.subData`, [
            { subTask: "", timeSpent: "" },
            { subTask: "", timeSpent: "" },
            { subTask: "", timeSpent: "" },
          ]);
        }
      });
    }
  }, [fetchedActivities, processedFormData?.coreFocusAreas, setValue, watch]);

  const watchedCoreFocusAreas = watch("coreFocusAreas");
  useEffect(() => {
    if (!employeeId) return;
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      setIsAutoSaving(true);
      setAutoSaveError(null);
      try {
        const latest = watchedCoreFocusAreas || [];
        const payload = latest.flatMap((focusArea) => {
          const coreId = focusArea?.id;
          const subData = Array.isArray(focusArea?.subData) ? focusArea.subData : [];

          return subData
            .filter((subTask) => {
              const hasTask = String(subTask?.subTask || "").trim().length > 0;
              const hasTime = String(subTask?.timeSpent ?? "").trim().length > 0;
              return hasTask || hasTime;
            })
            .map((subTask) => ({
              user_id: employeeId,
              core_focus_area_id: coreId,
              area: subTask.subTask,
              importance: Number(subTask.timeSpent) || 0,
            }));
        });

        if (payload.length === 0) return;

        const result = createCriticalActivity(payload);
        if (typeof result?.unwrap === "function") await result.unwrap();
        else await result;
      } catch (error) {
        const message =
          error?.data?.message || error?.message || "Auto-save failed.";
        setAutoSaveError(message);
        console.error("Auto-save failed (Step 3):", error);
      } finally {
        setIsAutoSaving(false);
      }
    }, 800);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [createCriticalActivity, employeeId, watchedCoreFocusAreas]);
  
  
  const onSubmit =async (data) => {
    let areaExceeds100 = false;

    coreFocusFields.forEach((field, index) => {
      const subTasks = getValues(`coreFocusAreas.${index}.subData`);
      const totalTimeForArea = subTasks.reduce(
        (subAcc, subTask) => subAcc + (Number(subTask.timeSpent) || 0),
        0
      );

      if (totalTimeForArea > 100) {
        //Alert message removed
        areaExceeds100 = true; //Set the flag to true
      }
      if (totalTimeForArea < 100) {
        //Alert message removed
        areaExceeds100 = true; //Set the flag to true
      }
    });

    if (areaExceeds100) {
      // Do not submit the form if there are any validation errors or if an area exceeds 100%
      return;
    }
  // if (isSubmitDisabled) return;
    try { 
      
      const payload = data.coreFocusAreas.flatMap(focusArea =>
              focusArea.subData.map(subTask => ({
                user_id: employeeId,
                core_focus_area_id: focusArea.id,
                area: subTask.subTask,
                importance: subTask.timeSpent,
              }))
            );
      
      await createCriticalActivity(payload);
      if (draftKey) localStorage.removeItem(draftKey);
      // console.log("Role review created successfully:", response);
      onNext(); // Proceed to the next step
    } catch (error) {
      console.error("Failed to create role review:", error);
      // Handle error (e.g., show an error message to the user)
    }

    // onNext();
  };
  const saveDataToBackend = async (data) => {
    const transformedData = data.coreFocusAreas.flatMap((focusArea) =>
      focusArea.subData.map((subTask) => ({
        user_id: employeeId,
        core_focus_area_id: focusArea.id,
        area: subTask.subTask,
        importance: subTask.timeSpent,
      }))
    );
  
    try {
      await createCriticalActivity(transformedData);
      console.log("Data saved successfully to backend");
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };
  const handlePrevious = async (data) => {
    try {
      await saveDataToBackend(data);
      onPrev(); // Move to the previous step after saving data
    } catch (error) {
      console.error("Error saving data before going back:", error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormMiddleBoxWrap
        sx={{
          paddingTop: "30px",
          paddingBottom: "0px",
          marginBottom: "0",
          boxShadow: "0",
          position: "relative",
          zIndex: "1",
        }}
      >
        <RowWrap>
          <Col12Wrap>
            <CustomHead>
             What are the critical activities that happen within each of the core focus areas you defined?
            </CustomHead>
            <FormLabelWrap
                    sx={{
                      fontSize: "20px",
                      color: "#7C7C7C",
                      letterSpacing: "0.3px",
                      marginBottom: "0px",
                    }}
                  >
                  Now please list, in order of importance, the critical activities that make up each core focus area listed below.Be sure that each “core focus area” totals to 100%. We’re looking for how your time in that segment of work is allocated. 
                  </FormLabelWrap>
                  <FormLabelWrap
                    sx={{
                      fontSize: "20px",
                      color: "#7C7C7C",
                      letterSpacing: "0.3px",
                      marginBottom: "10px",
                    }}
                  >
                    If needed, there is a short video to guide you.{" "}
                    <Link
                      to="https://youtu.be/MauV2Ui6arQ"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "#1976d2", textDecoration: "underline", fontWeight: "bold" }}
                    >
                      Click here to watch video
                    </Link>
                  </FormLabelWrap>
          </Col12Wrap>
        </RowWrap>
      </FormMiddleBoxWrap>

      {coreFocusFields.map((field, index) => (
        <CoreFocusAreaActivitiesCard
          key={field.fieldId}
          control={control}
          register={register}
          watch={watch}
          getValues={getValues}
          errors={errors}
          focusArea={field}
          focusAreaIndex={index}
          employeeId={employeeId}
          createCriticalActivity={createCriticalActivity}
        />
      ))}
      {isAutoSaving && (
        <Box sx={{ maxWidth: "1030px", margin: "0 auto", display: "flex", justifyContent: "center" }}>
          <Typography sx={{ fontSize: "14px", color: "#7C7C7C" }}>
            Saving…
          </Typography>
        </Box>
      )}
      {autoSaveError && (
        <Box sx={{ maxWidth: "1030px", margin: "6px auto 0", display: "flex", justifyContent: "center" }}>
          <Typography sx={{ fontSize: "12px", color: "red" }}>
            {autoSaveError}
          </Typography>
        </Box>
      )}
      <FormBtnBoxWrap>
           <RowWrap>
             <Col12Wrap sx={{ textAlign: "center" }}>
              <CustomBtn
            type="button"
            variant="outlined"
            sx={{
              marginRight: "10px", // Add spacing between buttons
              "& svg": { marginRight: "10px" },
            }}
            onClick={() => handlePrevious(getValues())} // Call the onPrev function on click
          >
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14 7C14.5523 7 15 7.44772 15 8C15 8.55228 14.5523 9 14 9L14 7ZM0.292893 8.70711C-0.0976311 8.31658 -0.097631 7.68342 0.292893 7.29289L6.65685 0.928932C7.04738 0.538407 7.68054 0.538407 8.07107 0.928932C8.46159 1.31946 8.46159 1.95262 8.07107 2.34315L2.41421 8L8.07107 13.6569C8.46159 14.0474 8.46159 14.6805 8.07107 15.0711C7.68054 15.4616 7.04738 15.4616 6.65685 15.0711L0.292893 8.70711ZM14 9L1 9L1 7L14 7L14 9Z"
                fill="white"
              />
            </svg>
            Previous
          </CustomBtn>
              <CustomBtn
              type="submit"
              variant="outlined"
              
              sx={{ "& svg": { 
                marginLeft: "10px" 
              },
             
              
            
            }}
             
            >
              Next
              <svg
                width="15"
                height="16"
                viewBox="0 0 15 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 7C0.447715 7 -4.82823e-08 7.44772 0 8C4.82823e-08 8.55228 0.447715 9 1 9L1 7ZM14.7071 8.70711C15.0976 8.31658 15.0976 7.68342 14.7071 7.29289L8.34315 0.928932C7.95262 0.538407 7.31946 0.538407 6.92893 0.928932C6.53841 1.31946 6.53841 1.95262 6.92893 2.34315L12.5858 8L6.92893 13.6569C6.53841 14.0474 6.53841 14.6805 6.92893 15.0711C7.31946 15.4616 7.95262 15.4616 8.34315 15.0711L14.7071 8.70711ZM1 9L14 9L14 7L1 7L1 9Z"
                  fill="white"
                />
              </svg>
            </CustomBtn>
            </Col12Wrap>
          </RowWrap>
        </FormBtnBoxWrap>
    </form>
  );
};

export default Step3;

const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));
const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));
const FormMiddleBoxWrap = styled(Box)(() => ({
  padding: "50px 150px",
  background: "#ffffff",
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: "16px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
    paddingLeft: "100px",
    paddingRight: "100px",
  },
  "@media (max-width: 991px)": {
    paddingLeft: "50px",
    paddingRight: "50px",
  },
  "@media (max-width: 575px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
  },
}));
const FormBtnBoxWrap = styled(Box)(() => ({
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginTop: "30px",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
  },
}));
const FormLabelWrap = styled(Typography)(() => ({
  fontSize: "20px",
  fontWeight: "500",
  lineHeight: "1.445",
  color: "#000000",
  marginBottom: "10px",
}));
const CustomBtn = styled(Button)(() => ({
  width: "100%",
  fontSize: "22px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "10px",
  // padding: "19px 20px",
  // maxWidth: "300px",
  maxWidth: "150px",
  padding: "10px 15px",
  boxShadow: "none !important",
}));
const CustomHead = styled(Typography)(() => ({
  fontSize: "26px",
  fontWeight: "600",
  lineHeight: "1.445",
  color: "#1085AB",
  "@media (max-width: 575px)": {
    fontSize: "22px",
  },
}));


